import React from "react";
import { Box, Text, IcoMoon, KeyboardArrowLeftIcon, KeyboardArrowRightIcon } from "@usap/uicomponents";
import PropTypes from "prop-types";
import BoxWithBorder from "../common/BoxWithBorder";
import { seoCommonIds } from "../../config/seo_common";

function getPagingRange(current, { min = 1, total = 20, length = 5 } = {}) {
  let pagingLength = length;
  if (pagingLength > total) pagingLength = total;
  let start = current - Math.floor(pagingLength / 2);
  start = Math.max(start, min);
  start = Math.min(start, min + total - pagingLength);
  return Array.from({ length: pagingLength }, (_, i) => start + i);
}

export default function Pagination(props) {
  const {
    wrap,
    pageNumber,
    totalPages,
    position = "center",
    totalRecords,
    shownRecords,
    commonSize = "36px",
    callback = () => {},
    dataTestId = null,
    isDesktop,
  } = props;
  const paginationArray = getPagingRange(pageNumber, { total: totalPages });

  const previous = () => {
    if (pageNumber === 1) return "";
    return callback(pageNumber - 1, "Previous");
  };

  const next = () => {
    if (pageNumber >= totalPages) return "";
    return callback(pageNumber + 1, "Next");
  };

  const changePage = currentPageNumber => {
    if (currentPageNumber === pageNumber) return "";
    return callback(currentPageNumber);
  };

  const isDisabled = paginationArray.length === 1;

  const paginationDataTestId = dataTestId ? `${dataTestId}-` : ''
  const responsiveDataTestId = `${(typeof isDesktop !== 'undefined' && (isDesktop ? "desktop-" : "wom-")) || ''}${paginationDataTestId}`

  return (
    <Box
      id="PaginationSection"
      justify={position}
      direction={"column"}
      align="center"
      wrap={wrap}
    >
      <Box id="NavigationBar" direction="row" gap="xxsmall" pad="small">
        { !isDisabled && (
          <BoxWithBorder
            disabled={pageNumber === 1}
            id={seoCommonIds.pagination.replace(`{{key}}`, "prev")}
            data-testid={`${responsiveDataTestId}${seoCommonIds.pagination.replace('{{key}}', 'prev')}`}
            height={commonSize}
            width="90px"
            pointer={true}
            margin={{ horizontal: "xsmall" }}
            count={
              <Box
                direction="row"
                align="center"
                justify="center"
                onClick={previous}
              >
                <IcoMoon
                  icon={KeyboardArrowLeftIcon}
                  color={pageNumber === 1 ? "status-disabled" : "dark-2"}
                  size="24px"
                />
                {wrap && (
                  <Text
                    margin={{ right: "small" }}
                    color={pageNumber === 1 ? "status-disabled" : "dark-2"}
                    size="small"
                  >
                    {"Previous"}
                  </Text>
                )}
              </Box>
            }
          />
        )}

        {!isDisabled &&
          paginationArray.map((count, index) => {
            return (
              <BoxWithBorder
                id={seoCommonIds.pagination.replace(`{{key}}`, index + 1)}
                data-testid={`${responsiveDataTestId}pagination-${count}`}
                height={commonSize}
                width={commonSize}
                pointer={true}
                margin={{ horizontal: "xsmall" }}
                selected={pageNumber === count}
                key={`${pageNumber}${index.toString()}`}
                count={count}
                onClick={() => changePage(count)}
              />
            );
          })}

        { !isDisabled && (
          <BoxWithBorder
            id={seoCommonIds.pagination.replace(`{{key}}`, "next")}
            data-testid={`${responsiveDataTestId}${seoCommonIds.pagination.replace('{{key}}', 'next')}`}
            height={commonSize}
            width="78px"
            disabled={pageNumber === totalPages}
            pointer={true}
            margin={{ horizontal: "xsmall" }}
            count={
              <Box
                direction="row"
                align="center"
                justify="center"
                onClick={next}
              >
                {wrap && (
                  <Text
                    textAlign="center"
                    color={
                      pageNumber === totalPages ? "status-disabled" : "dark-2"
                    }
                    size="small"
                  >
                    {"Next"}
                  </Text>
                )}
                <IcoMoon
                  color={
                    pageNumber === totalPages ? "status-disabled" : "dark-2"
                  }
                  icon={KeyboardArrowRightIcon}
                  size="24px"
                />
              </Box>
            }
          />
        )}
      </Box>
      <Box margin={{ vertical: "small" }}>
        <Text
          size="xsmall"
          color="dark-2"
        >{`Page ${pageNumber} of ${totalPages} | Showing ${shownRecords *
          pageNumber -
          shownRecords +
          1} - ${
          shownRecords * pageNumber < totalRecords
            ? shownRecords * pageNumber
            : totalRecords
        } of ${totalRecords} results`}</Text>
      </Box>
    </Box>
  );
}

Pagination.propTypes = {
  wrap: PropTypes.bool,
  pageNumber: PropTypes.number.isRequired,
  totalPages: PropTypes.number.isRequired,
  totalRecords: PropTypes.number.isRequired,
  shownRecords: PropTypes.number.isRequired,
  position: PropTypes.oneOf(["start", "center", "end"]),
  border: PropTypes.bool,
  commonSize: PropTypes.string,
  callback: PropTypes.func
};
